/**
 * Import/Export Module
 * Section 6.3 - Batch data operations
 */

import { useState, useRef } from 'react';
import oilSpecsData from '../../database/oil_specs.json';

function ImportExport() {
  const [activeTab, setActiveTab] = useState('export');
  const [exportFilters, setExportFilters] = useState({
    format: 'json',
    category: '',
    yearFrom: '',
    yearTo: '',
    make: ''
  });
  // eslint-disable-next-line no-unused-vars
  const [importData, setImportData] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importError, setImportError] = useState(null);
  const fileInputRef = useRef(null);

  // Get unique makes for filter
  const makes = [...new Set(oilSpecsData.map(s => s.make))].sort();

  const handleExport = () => {
    let data = [...oilSpecsData];

    // Apply filters
    if (exportFilters.category) {
      data = data.filter(s => s.brand_category === exportFilters.category);
    }
    if (exportFilters.yearFrom) {
      data = data.filter(s => s.year >= parseInt(exportFilters.yearFrom));
    }
    if (exportFilters.yearTo) {
      data = data.filter(s => s.year <= parseInt(exportFilters.yearTo));
    }
    if (exportFilters.make) {
      data = data.filter(s => s.make === exportFilters.make);
    }

    let content, filename, type;

    if (exportFilters.format === 'json') {
      content = JSON.stringify(data, null, 2);
      filename = 'oil_specs_export.json';
      type = 'application/json';
    } else {
      // CSV export
      const headers = Object.keys(data[0] || {}).join(',');
      const rows = data.map(row =>
        Object.values(row).map(v => {
          if (v === null || v === undefined) return '';
          if (typeof v === 'string' && v.includes(',')) return `"${v}"`;
          return v;
        }).join(',')
      );
      content = [headers, ...rows].join('\n');
      filename = 'oil_specs_export.csv';
      type = 'text/csv';
    }

    // Download file
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (!Array.isArray(data)) {
          throw new Error('Import data must be an array');
        }

        setImportData(data);
        setImportError(null);

        // Generate preview
        const preview = validateImport(data);
        setImportPreview(preview);
      } catch (error) {
        setImportError(`Invalid JSON file: ${error.message}`);
        setImportData(null);
        setImportPreview(null);
      }
    };
    reader.readAsText(file);
  };

  const validateImport = (data) => {
    const results = {
      total: data.length,
      valid: 0,
      invalid: 0,
      duplicates: 0,
      new: 0,
      updates: 0,
      errors: []
    };

    const existingKeys = new Set(
      oilSpecsData.map(s => `${s.year}_${s.make}_${s.model}_${s.engine_code}`)
    );

    data.forEach((record, index) => {
      // Check required fields
      const requiredFields = ['year', 'make', 'model', 'engine_code', 'oil_type', 'oil_capacity_qts', 'brand_category'];
      const missingFields = requiredFields.filter(f => !record[f]);

      if (missingFields.length > 0) {
        results.invalid++;
        results.errors.push({
          row: index + 1,
          type: 'missing_fields',
          message: `Missing: ${missingFields.join(', ')}`
        });
        return;
      }

      // Check for duplicates in import
      const key = `${record.year}_${record.make}_${record.model}_${record.engine_code}`;
      if (existingKeys.has(key)) {
        results.updates++;
      } else {
        results.new++;
      }

      results.valid++;
    });

    return results;
  };

  const handleImport = () => {
    alert('Import functionality requires backend API (Phase 5)');
  };

  return (
    <div className="import-export">
      <div className="admin-page-header">
        <h1>Import/Export</h1>
        <p>Batch data operations for vehicle database</p>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'export' ? 'active' : ''}`}
          onClick={() => setActiveTab('export')}
        >
          Export Data
        </button>
        <button
          className={`tab ${activeTab === 'import' ? 'active' : ''}`}
          onClick={() => setActiveTab('import')}
        >
          Import Data
        </button>
      </div>

      {/* Export Tab */}
      {activeTab === 'export' && (
        <div className="admin-card">
          <h2>Export Vehicle Database</h2>

          <div className="export-grid">
            <div className="admin-form-group">
              <label>Format</label>
              <select
                className="admin-select"
                value={exportFilters.format}
                onChange={(e) => setExportFilters(f => ({ ...f, format: e.target.value }))}
              >
                <option value="json">JSON</option>
                <option value="csv">CSV</option>
              </select>
            </div>

            <div className="admin-form-group">
              <label>Category</label>
              <select
                className="admin-select"
                value={exportFilters.category}
                onChange={(e) => setExportFilters(f => ({ ...f, category: e.target.value }))}
              >
                <option value="">All Categories</option>
                <option value="standard">Standard</option>
                <option value="european">European</option>
                <option value="truck">Truck</option>
              </select>
            </div>

            <div className="admin-form-group">
              <label>Year From</label>
              <input
                type="number"
                className="admin-input"
                value={exportFilters.yearFrom}
                onChange={(e) => setExportFilters(f => ({ ...f, yearFrom: e.target.value }))}
                placeholder="e.g., 2015"
              />
            </div>

            <div className="admin-form-group">
              <label>Year To</label>
              <input
                type="number"
                className="admin-input"
                value={exportFilters.yearTo}
                onChange={(e) => setExportFilters(f => ({ ...f, yearTo: e.target.value }))}
                placeholder="e.g., 2025"
              />
            </div>

            <div className="admin-form-group">
              <label>Make</label>
              <select
                className="admin-select"
                value={exportFilters.make}
                onChange={(e) => setExportFilters(f => ({ ...f, make: e.target.value }))}
              >
                <option value="">All Makes</option>
                {makes.map(make => (
                  <option key={make} value={make}>{make}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="export-info">
            <p>
              Current database: <strong>{oilSpecsData.length.toLocaleString()}</strong> records
            </p>
          </div>

          <button className="admin-btn admin-btn-primary" onClick={handleExport}>
            Download Export
          </button>
        </div>
      )}

      {/* Import Tab */}
      {activeTab === 'import' && (
        <div className="admin-card">
          <h2>Import Vehicle Data</h2>

          <div className="import-dropzone" onClick={() => fileInputRef.current?.click()}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <div className="dropzone-content">
              <span className="dropzone-icon">📁</span>
              <p>Click to select a JSON file</p>
              <span className="dropzone-hint">Only .json files are supported</span>
            </div>
          </div>

          {importError && (
            <div className="import-error">
              {importError}
            </div>
          )}

          {importPreview && (
            <div className="import-preview">
              <h3>Import Preview</h3>
              <div className="preview-stats">
                <div className="preview-stat">
                  <span className="stat-value">{importPreview.total}</span>
                  <span className="stat-label">Total Records</span>
                </div>
                <div className="preview-stat success">
                  <span className="stat-value">{importPreview.valid}</span>
                  <span className="stat-label">Valid</span>
                </div>
                <div className="preview-stat warning">
                  <span className="stat-value">{importPreview.new}</span>
                  <span className="stat-label">New</span>
                </div>
                <div className="preview-stat info">
                  <span className="stat-value">{importPreview.updates}</span>
                  <span className="stat-label">Updates</span>
                </div>
                <div className="preview-stat error">
                  <span className="stat-value">{importPreview.invalid}</span>
                  <span className="stat-label">Invalid</span>
                </div>
              </div>

              {importPreview.errors.length > 0 && (
                <div className="preview-errors">
                  <h4>Validation Errors</h4>
                  <ul>
                    {importPreview.errors.slice(0, 10).map((error, i) => (
                      <li key={i}>
                        Row {error.row}: {error.message}
                      </li>
                    ))}
                    {importPreview.errors.length > 10 && (
                      <li className="more-errors">
                        ...and {importPreview.errors.length - 10} more errors
                      </li>
                    )}
                  </ul>
                </div>
              )}

              <div className="import-actions">
                <button
                  className="admin-btn admin-btn-secondary"
                  onClick={() => {
                    setImportData(null);
                    setImportPreview(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  Clear
                </button>
                <button
                  className="admin-btn admin-btn-primary"
                  onClick={handleImport}
                  disabled={importPreview.invalid > 0}
                >
                  Import {importPreview.valid} Records
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        .tabs {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .tab {
          padding: 0.75rem 1.5rem;
          background: #f3f4f6;
          border: none;
          border-radius: 8px 8px 0 0;
          font-size: 0.9rem;
          font-weight: 500;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s;
        }

        .tab.active {
          background: white;
          color: #111827;
          box-shadow: 0 -2px 0 #2563eb inset;
        }

        .export-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .export-info {
          padding: 1rem;
          background: #f3f4f6;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .export-info p {
          margin: 0;
          color: #6b7280;
        }

        .import-dropzone {
          border: 2px dashed #d1d5db;
          border-radius: 12px;
          padding: 3rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: 1.5rem;
        }

        .import-dropzone:hover {
          border-color: #2563eb;
          background: #f8fafc;
        }

        .dropzone-icon {
          font-size: 3rem;
        }

        .dropzone-content p {
          margin: 1rem 0 0.5rem;
          font-weight: 500;
          color: #374151;
        }

        .dropzone-hint {
          font-size: 0.8rem;
          color: #9ca3af;
        }

        .import-error {
          padding: 1rem;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #dc2626;
          margin-bottom: 1.5rem;
        }

        .import-preview {
          padding: 1.5rem;
          background: #f9fafb;
          border-radius: 12px;
        }

        .import-preview h3 {
          margin: 0 0 1rem 0;
        }

        .preview-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .preview-stat {
          padding: 1rem;
          background: white;
          border-radius: 8px;
          text-align: center;
        }

        .preview-stat .stat-value {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
        }

        .preview-stat .stat-label {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .preview-stat.success { border-left: 4px solid #10b981; }
        .preview-stat.warning { border-left: 4px solid #f59e0b; }
        .preview-stat.info { border-left: 4px solid #3b82f6; }
        .preview-stat.error { border-left: 4px solid #ef4444; }

        .preview-errors {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }

        .preview-errors h4 {
          margin: 0 0 0.75rem 0;
          color: #991b1b;
        }

        .preview-errors ul {
          margin: 0;
          padding-left: 1.25rem;
          color: #7f1d1d;
          font-size: 0.875rem;
        }

        .more-errors {
          color: #9ca3af;
          font-style: italic;
        }

        .import-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
        }
      `}</style>
    </div>
  );
}

export default ImportExport;
